import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatIntegrationProvider, IChatIntegrations } from '@interfaces/IChatIntegrations';
import { disconnectApp, fetchChatIntegrations, setAppEnabled, startConnect } from '@apis/chatIntegrations';
import { ALL_COMING_SOON } from '@constants/chatIntegrations';

const CHAT_INTEGRATIONS_STALE_MS = 5 * 60_000;
const CHAT_INTEGRATIONS_KEY = ['chat-integrations'];

export interface IChatIntegrationsApi {
  status: IChatIntegrations;
  connect: (provider: ChatIntegrationProvider) => void;
  setEnabled: (provider: ChatIntegrationProvider, enabled: boolean) => void;
  disconnect: (provider: ChatIntegrationProvider) => void;
  saveFailed: ChatIntegrationProvider | null; // An app whose last change did not save, to say so
  saving: ChatIntegrationProvider | null; // An app whose change is still saving, its controls held until the list is read again
  loadFailed: boolean; // The list could not be read: the apps shown are a stand-in, not the person's own
}

/** A change to one app's connection: its switch, or disconnecting it. */
type ConnectionChange = { provider: ChatIntegrationProvider } & ({ enabled: boolean } | { disconnect: true });

const saveChange = (change: ConnectionChange): Promise<void> =>
  'disconnect' in change ? disconnectApp(change.provider) : setAppEnabled(change.provider, change.enabled);

/**
 * The person's chat integrations, from the server. Until it answers, every app
 * reads "Coming soon", so nothing offers a connect it cannot back.
 */
export function useChatIntegrations(): IChatIntegrationsApi {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: CHAT_INTEGRATIONS_KEY,
    queryFn: ({ signal }) => fetchChatIntegrations(signal),
    staleTime: CHAT_INTEGRATIONS_STALE_MS,
  });
  const [saveFailed, setSaveFailed] = useState<ChatIntegrationProvider | null>(null);

  // The popover shows what the server holds: after every change, saved or not, the list is read again.
  const change = useMutation({
    mutationFn: saveChange,
    onMutate: () => setSaveFailed(null),
    onError: (_err, { provider }) => setSaveFailed(provider),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CHAT_INTEGRATIONS_KEY }),
  });

  return {
    status: query.data ?? ALL_COMING_SOON,
    connect: startConnect,
    setEnabled: (provider, enabled) => change.mutate({ provider, enabled }),
    disconnect: (provider) => change.mutate({ provider, disconnect: true }),
    saveFailed,
    saving: change.isPending ? change.variables.provider : null,
    loadFailed: query.isError,
  };
}
